import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import config from "@/config/environment";
import { decodeTokenService } from "@/services/decodeTokenService";
import type { GrowthIntelligenceBrand, GrowthIntelligenceAd, SpyAd } from "../types/adsSpy.types";

const growthIntelligenceApi = axios.create({
  baseURL: config.apiUrlGrowthIntelligence,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

growthIntelligenceApi.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) => {
    const token = decodeTokenService.getToken();
    const ownerId = decodeTokenService.getUserIdFromToken() || localStorage.getItem("user_id") || "";
    requestConfig.headers = requestConfig.headers || {};
    if (token) requestConfig.headers.Authorization = `Bearer ${token}`;
    if (ownerId) requestConfig.headers["owner-id"] = ownerId;
    return requestConfig;
  },
  (error: AxiosError) => Promise.reject(error)
);

export const growthIntelligenceService = {
  async getRadar(params?: { country?: string; limit?: number; ads_per_brand?: number }): Promise<GrowthIntelligenceBrand[]> {
    const { data } = await growthIntelligenceApi.get("/radar", { params });
    return data.data || [];
  },

  async getBrandAds(brandId: number, params?: { country?: string; limit?: number }): Promise<GrowthIntelligenceAd[]> {
    const { data } = await growthIntelligenceApi.get(`/brands/${brandId}/ads`, { params });
    return data.data || [];
  },

  async getFavorites(params?: { country?: string; limit?: number }): Promise<GrowthIntelligenceAd[]> {
    const { data } = await growthIntelligenceApi.get("/favorites", { params });
    return data.data || [];
  },

  async searchAds(params: { q: string; country?: string; limit?: number }): Promise<GrowthIntelligenceAd[]> {
    const { data } = await growthIntelligenceApi.get("/search", { params });
    return data.data || [];
  },

  async addFavorite(adId: number): Promise<void> {
    await growthIntelligenceApi.post("/favorites", { ad_id: adId });
  },

  async removeFavorite(adId: number): Promise<void> {
    await growthIntelligenceApi.delete(`/favorites/${adId}`);
  },
};

export const mapGrowthAdToSpyAd = (ad: GrowthIntelligenceAd): SpyAd => ({
  id: ad.id,
  brand_id: ad.brand_id,
  library_id: ad.library_id || "",
  ad_archive_id: ad.ad_archive_id || ad.external_ad_id,
  image_url: ad.image_url || null,
  video_url: ad.video_url || null,
  thumbnail_url: ad.thumbnail_url || ad.image_url || null,
  media_type: ad.media_type,
  headline: ad.headline || "",
  ad_copy: ad.ad_copy || "",
  cta_text: ad.cta_text || "",
  landing_page_url: ad.landing_page_url || "",
  platforms: ad.platforms || "",
  start_date: ad.start_date || "",
  end_date: ad.end_date || "",
  impression_rank: ad.radar_rank || 0,
  ai_analysis: null,
  is_bookmarked: ad.is_favorite,
  brand_name: ad.brand_name || "",
  country: ad.country,
  page_id: ad.page_id || null,
  scraped_at: ad.last_seen_at,
});
